import { ReactNode } from 'react';
import { useRole } from '../../contexts/RoleContext';
import { Permission, UserRole } from '../../api/client';
import { ShieldX, Loader2 } from 'lucide-react';

interface RoleGuardProps {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Conditionally renders children based on user permission.
 * If user doesn't have permission, renders nothing (or fallback).
 * Use this for hiding UI elements like buttons.
 */
export function RoleGuard({ permission, children, fallback = null }: RoleGuardProps) {
  const { can, loading } = useRole();

  if (loading) {
    return null;
  }

  if (!can(permission)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

interface RequirePermissionProps {
  permission: Permission;
  children: ReactNode;
}

/**
 * Requires a permission to view page content.
 * Shows Access Denied page if permission is missing.
 * Use this for protecting entire pages/sections.
 */
export function RequirePermission({ permission, children }: RequirePermissionProps) {
  const { can, loading, role } = useRole();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (role === 'unauthorized') {
    return <AccessDenied message="You do not have access to this application." />;
  }

  if (!can(permission)) {
    return <AccessDenied message={`You don't have permission to access this page.`} />;
  }

  return <>{children}</>;
}

interface RequireRoleProps {
  roles: UserRole[];
  children: ReactNode;
}

/**
 * Requires one of the specified roles to view content.
 * Shows Access Denied page if role doesn't match.
 */
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { role, loading } = useRole();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!role || !roles.includes(role)) {
    return <AccessDenied message="You don't have the required role to access this page." />;
  }

  return <>{children}</>;
}

interface AccessDeniedProps {
  message?: string;
}

/**
 * Access Denied page component
 */
export function AccessDenied({ message = 'You do not have permission to access this page.' }: AccessDeniedProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
      <ShieldX className="w-16 h-16 text-red-400 mb-4" />
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
      <p className="text-gray-600 max-w-md">{message}</p>
      <p className="text-sm text-gray-500 mt-4">
        If you believe this is an error, please contact your administrator.
      </p>
    </div>
  );
}
